import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function VirtualCards() {
  const [cards, setCards] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showNfcModal, setShowNfcModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [transactionCode, setTransactionCode] = useState('');
  const [formData, setFormData] = useState({
    account_id: '',
    card_type: 'visa',
    daily_limit: 1000,
    monthly_limit: 5000
  });

  const token = localStorage.getItem('token');

  const cardTypeInfo = {
    visa: { name: 'Visa', color: 'blue', icon: '💳' },
    mastercard: { name: 'Mastercard', color: 'orange', icon: '💳' },
    cb: { name: 'Carte Bancaire', color: 'green', icon: '💳' }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [cardsRes, accountsRes] = await Promise.all([
        axios.get(`${API_URL}/cards`, config),
        axios.get(`${API_URL}/accounts`, config)
      ]);
      setCards(cardsRes.data);
      setAccounts(accountsRes.data.filter(acc => acc.status === 'active'));
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API_URL}/cards`, formData, config);
      alert('Carte virtuelle créée avec succès !');
      setShowForm(false);
      setFormData({
        account_id: '',
        card_type: 'visa',
        daily_limit: 1000,
        monthly_limit: 5000
      });
      loadData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleBlock = async (cardId, currentStatus) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const newStatus = currentStatus === 'active' ? 'blocked' : 'active';
      await axios.patch(`${API_URL}/cards/${cardId}`, { status: newStatus }, config);
      loadData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette carte ?')) return;
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_URL}/cards/${cardId}`, config);
      alert('Carte supprimée avec succès');
      loadData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleNfcPayment = (card) => {
    // Générer un code de transaction unique
    const timestamp = Date.now();
    const randomCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    const code = `BKB-${card.id.substring(0, 8)}-${timestamp}-${randomCode}`;
    
    setSelectedCard(card);
    setTransactionCode(code);
    setShowNfcModal(true);
  };

  const closeNfcModal = () => {
    setShowNfcModal(false);
    setSelectedCard(null);
    setTransactionCode('');
  };

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Cartes Bancaires</h1>
        <div className="flex gap-3">
          <button
            onClick={() => window.open('/mobile-pay', '_blank')}
            className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 rounded-lg font-semibold hover:from-yellow-400 hover:to-yellow-500 transition-colors flex items-center gap-2"
          >
            📱 App Mobile
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            {showForm ? 'Annuler' : '+ Nouvelle carte'}
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-yellow-50 border-l-4 border-blue-500 p-6 rounded-lg mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start flex-1">
            <span className="text-3xl mr-4">📱</span>
            <div>
              <p className="font-semibold text-blue-900 text-lg mb-2">Application Mobile BKB Pay</p>
              <p className="text-sm text-blue-700 mb-3">
                Téléchargez l'application mobile pour payer directement sur les bornes de paiement avec vos cartes virtuelles. 
                Paiement NFC instantané et sécurisé !
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => window.open('/install-app', '_blank')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  📲 Télécharger l'app mobile
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
        <div className="flex items-start">
          <span className="text-2xl mr-3">💳</span>
          <div>
            <p className="font-semibold text-blue-900">Cartes bancaires virtuelles NFC</p>
            <p className="text-sm text-blue-700">
              Créez des cartes virtuelles Visa, Mastercard ou CB pour vos paiements sans contact (NFC), 
              en ligne et dans les commerces. Contrôlez vos limites et bloquez instantanément si nécessaire.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Créer une carte virtuelle</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compte à associer *
              </label>
              <select
                value={formData.account_id}
                onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Sélectionner un compte</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.account_type} - {acc.balance.toFixed(2)} € - N° {acc.account_number_display || acc.reference_number}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de carte *
              </label>
              <select
                value={formData.card_type}
                onChange={(e) => setFormData({ ...formData, card_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="visa">Visa</option>
                <option value="mastercard">Mastercard</option>
                <option value="cb">Carte Bancaire (CB)</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Limite journalière (€) *
                </label>
                <input
                  type="number"
                  step="100"
                  value={formData.daily_limit}
                  onChange={(e) => setFormData({ ...formData, daily_limit: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Limite mensuelle (€) *
                </label>
                <input
                  type="number"
                  step="100"
                  value={formData.monthly_limit}
                  onChange={(e) => setFormData({ ...formData, monthly_limit: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Créer la carte
            </button>
          </form>
        </div>
      )}

      {/* Cards List */}
      <div className="grid md:grid-cols-2 gap-6">
        {cards.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center md:col-span-2">
            <p className="text-gray-500">Aucune carte bancaire créée</p>
          </div>
        ) : (
          cards.map((card) => {
            const cardInfo = cardTypeInfo[card.card_type];
            const isBlocked = card.status === 'blocked';
            
            return (
              <div
                key={card.id}
                className={`bg-gradient-to-br from-${cardInfo.color}-600 to-${cardInfo.color}-800 rounded-2xl p-6 text-white shadow-2xl relative overflow-hidden`}
              >
                {isBlocked && (
                  <div className="absolute top-0 left-0 right-0 bg-red-600 text-center py-1 text-xs font-bold">
                    🚫 CARTE BLOQUÉE
                  </div>
                )}

                <div className={`${isBlocked ? 'mt-6' : ''}`}>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <p className="text-sm opacity-80">{cardInfo.name}</p>
                      <p className="text-2xl font-bold">{card.cardholder_name}</p>
                    </div>
                    <span className="text-4xl">{cardInfo.icon}</span>
                  </div>

                  <div className="mb-6">
                    <p className="text-2xl font-mono tracking-wider">{card.card_number}</p>
                  </div>

                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-xs opacity-70">Expire le</p>
                      <p className="text-lg font-semibold">{card.expiry_date}</p>
                    </div>
                    <div>
                      <p className="text-xs opacity-70">CVV</p>
                      <p className="text-lg font-semibold font-mono">{card.cvv}</p>
                    </div>
                    <div className="text-right">
                      {card.nfc_enabled && (
                        <span className="text-2xl" title="NFC activé">📡</span>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/20">
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                      <div>
                        <p className="opacity-70">Dépensé aujourd'hui</p>
                        <p className="font-semibold">{card.daily_spent.toFixed(2)}€ / {card.daily_limit}€</p>
                      </div>
                      <div>
                        <p className="opacity-70">Dépensé ce mois</p>
                        <p className="font-semibold">{card.monthly_spent.toFixed(2)}€ / {card.monthly_limit}€</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleNfcPayment(card)}
                        disabled={isBlocked || !card.nfc_enabled}
                        className={`flex-1 py-2 bg-green-500/80 hover:bg-green-500 rounded-lg font-medium transition-colors text-sm ${
                          (isBlocked || !card.nfc_enabled) ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title={!card.nfc_enabled ? 'NFC non activé sur cette carte' : 'Payer avec NFC'}
                      >
                        📱 Payer NFC
                      </button>
                      <button
                        onClick={() => handleBlock(card.id, card.status)}
                        className="flex-1 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors text-sm"
                      >
                        {isBlocked ? '🔓 Débloquer' : '🔒 Bloquer'}
                      </button>
                      <button
                        onClick={() => handleDelete(card.id)}
                        className="px-4 py-2 bg-red-600/80 hover:bg-red-600 rounded-lg font-medium transition-colors text-sm"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal Paiement NFC */}
      {showNfcModal && selectedCard && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={closeNfcModal}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">📱 Paiement NFC</h2>
              <button
                onClick={closeNfcModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Info carte */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-600 mb-2">Carte sélectionnée</p>
              <p className="font-semibold text-gray-900">
                {selectedCard.card_type.toUpperCase()} •••• {selectedCard.card_number.slice(-4)}
              </p>
              <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                <div>
                  <p className="text-gray-600">Limite restante (jour)</p>
                  <p className="font-semibold text-green-600">
                    {(selectedCard.daily_limit - selectedCard.daily_spent).toFixed(2)}€
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Limite restante (mois)</p>
                  <p className="font-semibold text-green-600">
                    {(selectedCard.monthly_limit - selectedCard.monthly_spent).toFixed(2)}€
                  </p>
                </div>
              </div>
            </div>

            {/* QR Code */}
            <div className="text-center mb-6">
              <div className="inline-block p-6 bg-white border-4 border-gray-200 rounded-2xl shadow-inner">
                <QRCodeSVG 
                  value={transactionCode}
                  size={220}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <p className="text-xs text-gray-500 mt-3 font-mono">
                Code: {transactionCode.substring(0, 20)}...
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg mb-6">
              <p className="font-semibold text-blue-900 mb-2">📋 Instructions</p>
              <ol className="text-sm text-blue-800 space-y-1">
                <li>1. Présentez ce QR code au terminal de paiement (TPE)</li>
                <li>2. Le commerçant scanne le code avec son terminal</li>
                <li>3. Entrez le montant sur le TPE</li>
                <li>4. Confirmez le paiement</li>
                <li>5. Vous recevrez une notification immédiate</li>
              </ol>
            </div>

            {/* Sécurité */}
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg mb-6">
              <p className="font-semibold text-green-900 mb-2">🛡️ Sécurité</p>
              <ul className="text-sm text-green-800 space-y-1">
                <li>✓ Code unique et temporaire (expire dans 2 minutes)</li>
                <li>✓ Utilisable une seule fois</li>
                <li>✓ Limites de dépenses respectées</li>
                <li>✓ Transaction sécurisée et cryptée</li>
              </ul>
            </div>

            {/* Bouton Test TPE */}
            <div className="flex gap-3">
              <button
                onClick={() => window.open('/tpe-simulator', '_blank')}
                className="flex-1 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-medium hover:from-green-700 hover:to-green-800 transition-all shadow-lg"
              >
                🖥️ Tester sur TPE Simulateur
              </button>
              <button
                onClick={closeNfcModal}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>

            {/* Note */}
            <p className="text-xs text-gray-500 mt-4 text-center">
              💡 Compatible avec tous les terminaux de paiement équipés d'un scanner QR
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default VirtualCards;
